/**
 * Custom Hook for Dashboard Data
 * Manages all dashboard data fetching and state
 */

import { useState, useEffect, useCallback } from 'react';
import {
  dashboardService,
  DashboardStats,
  DashboardOrder,
  RevenueDataPoint,
} from '../services/dashboard.service';
import { menuTypesService } from '../services/menuTypes.service';

/**
 * Dashboard State Interface
 */
interface DashboardState {
  stats: DashboardStats | null;
  recentOrders: DashboardOrder[];
  revenueData: RevenueDataPoint[];
  loading: boolean;
  error: string | null;
}

/**
 * Date Range Interface
 */
interface DateRange {
  start: Date;
  end: Date;
}

/**
 * Custom Hook for Dashboard Data
 * Manages all dashboard data fetching and state
 */
export function useDashboard(tenantId: string, initialDateRange?: DateRange) {
  const [state, setState] = useState<DashboardState>({
    stats: null,
    recentOrders: [],
    revenueData: [],
    loading: true,
    error: null,
  });

  const [dateRange, setDateRange] = useState<DateRange>(
    initialDateRange || {
      start: new Date(new Date().setDate(new Date().getDate() - 30)), // Last 30 days
      end: new Date(),
    }
  );

  /**
   * Fetch dashboard statistics
   */
  const fetchStats = useCallback(async () => {
    if (!tenantId) {
      console.warn('useDashboard: Cannot fetch stats without tenantId');
      return;
    }
    try {
      // Fetch both dashboard stats and menu types in parallel
      const [statsResponse, menuTypesResponse] = await Promise.all([
        dashboardService.getStats(tenantId, dateRange),
        menuTypesService.getMenuTypes(tenantId),
      ]);

      if (statsResponse.success && statsResponse.data) {
        // Override totalMenus with actual menu types count
        const stats = {
          ...statsResponse.data,
          totalMenus: menuTypesResponse.success && menuTypesResponse.data
            ? menuTypesResponse.data.length
            : statsResponse.data.totalMenus,
        };

        setState((prev) => ({
          ...prev,
          stats,
          error: null,
        }));
      }
    } catch (error: any) {
      console.error('Error fetching dashboard stats:', error);
      setState((prev) => ({
        ...prev,
        error: error.message || 'Failed to load dashboard statistics',
      }));
    }
  }, [tenantId, dateRange]);

  /**
   * Fetch recent orders
   */
  const fetchRecentOrders = useCallback(async () => {
    if (!tenantId) {
      console.warn('useDashboard: Cannot fetch recent orders without tenantId');
      return;
    }
    try {
      const response = await dashboardService.getRecentOrders(tenantId, 3);
      if (response.success && response.data) {
        setState((prev) => ({
          ...prev,
          recentOrders: response.data!,
          error: null,
        }));
      }
    } catch (error: any) {
      console.error('Error fetching recent orders:', error);
      setState((prev) => ({
        ...prev,
        error: error.message || 'Failed to load recent orders',
      }));
    }
  }, [tenantId]);

  /*if (!tenantId) {
      console.warn('useDashboard: Cannot fetch revenue data without tenantId');
      return;
    }
    *
   * Fetch revenue data
   */
  const fetchRevenueData = useCallback(async () => {
    try {
      const response = await dashboardService.getRevenueData(tenantId, dateRange);
      if (response.success && response.data) {
        setState((prev) => ({
          ...prev,
          revenueData: response.data!,
          error: null,
        }));
      }
    } catch (error: any) {
      if (error?.status === 403) {
        // User lacks analytics_revenue permission — leave revenueData as empty array
        return;
      }
      console.error('Error fetching revenue data:', error);
      setState((prev) => ({
        ...prev,
        error: error.message || 'Failed to load revenue data',
      }));
    }
  }, [tenantId, dateRange]);

  /**
   * Fetch all dashboard data
   */
  const fetchDashboardData = useCallback(async () => {
    if (!tenantId) {
      console.warn('useDashboard: Cannot fetch dashboard data without tenantId');
      setState((prev) => ({ ...prev, loading: false, error: 'User tenant ID not available' }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Fetch all data in parallel
      await Promise.all([
        fetchStats(),
        fetchRecentOrders(),
        fetchRevenueData(),
      ]);
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      setState((prev) => ({
        ...prev,
        error: error.message || 'Failed to load dashboard data',
      }));
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [fetchStats, fetchRecentOrders, fetchRevenueData, tenantId]);

  /**
   * Refresh dashboard data
   */
  const refresh = useCallback(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  /**
   * Update date range and refetch data
   */
  const updateDateRange = useCallback((newDateRange: DateRange) => {
    console.log('📅 Updating date range:', newDateRange);
    setDateRange(newDateRange);
  }, []);

  /**
   * Fetch data on mount and when date range changes
   */
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    // State
    stats: state.stats,
    recentOrders: state.recentOrders,
    revenueData: state.revenueData,
    loading: state.loading,
    error: state.error,
    dateRange,

    // Actions
    refresh,
    updateDateRange,
  };
}